import React, {useState} from 'react';
import {
    Image,
    Modal,
    StyleSheet,
    Text, TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import {SCREEN_HEIGHT, SCREEN_WIDTH, WINDOW_WIDTH} from "../../../Config/Constants";
import {BORDER_COLOR, DATA_COLOR, HEADER_TITLE_COLOR, PRIMARY_BACKGROUND_COLOR} from "../../../Config/Colors";
import LoginIcon from "../../../Images/Icons/loguserin.png";
import LogoutIcon from "../../../Images/Icons/logout.png";
import CloseIcon from "../../../Images/Icons/close.png";
import UserService from "../../../Domain/Services/UserService";
import { userRepository } from "../../../Domain/Repositories/UserRepository";
import User from "../../../Domain/Models/User";
import {MODAL_INFO_MESSAGE_MILLISECONDS} from "../../../Config/Parameters";

let timer: NodeJS.Timeout | undefined;

/**
 * The Login Modal Props.
 */
interface LoginModalProps
{
    /**
     * Flag to toggle the modal.
     */
    show?: boolean;

    /**
     * Callback when the close button was pressed.
     */
    onCloseButtonSelected?: () => void;
}

/**
 * Renders the Login Modal.
 */
export default function LoginModal({
    show,
    onCloseButtonSelected,
}: LoginModalProps): JSX.Element
{
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    
    const onUsernameTextUpdated = (text: string): void =>
    {
        setUsername(text);
    };

    const onPasswordTextUpdated = (text: string): void =>
    {
        setPassword(text);
    };

    const onLoginButtonSelected = async (): Promise<void> =>
    {
        if (username.trim() === "")
        {
            updateMessage("The username field is empty!");
            return;
        }

        if (password.trim() === "")
        {
            updateMessage("The password field is empty!");
            return;
        }
        
        setMessage("Logging in. Please wait...");
        
        const user: User = {
            emailAddress: username,
            password: password,
        };
        
        // Login here...
        const result = await UserService.login(user);
        
        if (result.success)
        {
            updateMessage("Login successful!");
            
            userRepository.updateUser(user);
            return;
        }
        
        updateMessage("Failed to login. Please try again.")
    }
    
    const onLogoutButtonSelected = async (): Promise<void> =>
    {
        userRepository.removeUserDetails();
        updateMessage("Successfully logged out!");
    }

    const onCloseButtonPressed = (): void =>
    {
        if (onCloseButtonSelected !== undefined)
        {
            onCloseButtonSelected();
        }
    }

    const updateMessage = (message: string): void =>
    {
        if (timer !== undefined)
        {
            clearTimeout(timer);
            timer = undefined;
        }

        setMessage(message);

        timer = setTimeout(() =>
        {
            setMessage("");
        }, MODAL_INFO_MESSAGE_MILLISECONDS);
    }
    
    const onModalShown = (): void =>
    {
        setUsername("");
        setPassword("");
    }

    return (
        <Modal
            animationType="fade"
            visible={show}
            transparent
            statusBarTranslucent
            onShow={onModalShown}
        >
            <View style={styles.modal}>
                <View style={styles.modalContentParent}>
                    <View style={styles.sectionTitleView}>
                        <Text style={styles.titleText}>
                            Login
                        </Text>

                        <TouchableOpacity
                            style={styles.closeButton}
                            onPress={onCloseButtonPressed}
                        >
                            <Image
                                source={CloseIcon}
                                resizeMode={"contain"}
                                style={styles.icon}
                            />
                        </TouchableOpacity>
                    </View>

                    <View style={styles.textFieldView}>
                        <Text style={styles.labelText}>
                            Username
                        </Text>

                        <TextInput
                            placeholder="Username"
                            style={styles.input}
                            onChangeText={onUsernameTextUpdated}
                            placeholderTextColor={DATA_COLOR}
                            autoCompleteType="off"
                        />
                    </View>

                    <View style={styles.textFieldView}>
                        <Text style={styles.labelText}>
                            Password
                        </Text>

                        <TextInput
                            secureTextEntry
                            placeholder="Password"
                            style={styles.input}
                            onChangeText={onPasswordTextUpdated}
                            placeholderTextColor={DATA_COLOR}
                            autoCompleteType="off"
                        />
                    </View>
                    
                    <View style={styles.buttonView}>
                        <TouchableOpacity
                            onPress={onLoginButtonSelected}
                            style={[styles.button, {
                                marginBottom: '3%',
                            }]}
                        >
                            <Image
                                source={LoginIcon}
                                resizeMode={"contain"}
                                style={styles.icon}
                            />

                            <Text style={styles.updateButtonText}>
                                Log in
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            onPress={onLogoutButtonSelected}
                            style={styles.button}
                        >
                            <Image
                                source={LogoutIcon}
                                resizeMode={"contain"}
                                style={styles.icon}
                            />

                            <Text style={styles.updateButtonText}>
                                Log Out
                            </Text>
                        </TouchableOpacity>
                    </View>


                    <View style={styles.resultView}>
                        <Text style={styles.resultText}>
                            {message}
                        </Text>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    modal: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    modalContentParent: {
        width: '45%',
        height: '70%',
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
        borderWidth: 2,
        borderColor: BORDER_COLOR,
        borderRadius: SCREEN_WIDTH * 0.01,
    },
    sectionTitleView: {
        flex: 0.15,
        justifyContent: 'flex-start',
        borderBottomWidth: 2,
        borderBottomColor: BORDER_COLOR,
    },
    titleText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.02,
        fontWeight: "600",
        textAlign: 'left',
        paddingLeft: '5%',
        paddingTop: '5%',
    },
    closeButton: {
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        alignSelf: 'flex-end',
        marginRight: '2%',
        height: '40%',
        position: 'absolute',
        right: 0,
        top: '14%',
        width: undefined,
        aspectRatio: 1,
        borderRadius: SCREEN_HEIGHT,
        backgroundColor: HEADER_TITLE_COLOR,
    },
    textFieldView: {
        flex: 0.2,
        flexDirection: 'row',
    },
    buttonView: {
        flex: 0.3,
        justifyContent: 'center',
        alignItems: 'flex-end',
    },
    updateButtonText: {
        textAlign: 'left',
        alignSelf: 'center',
        fontSize: SCREEN_WIDTH * 0.014,
        color: PRIMARY_BACKGROUND_COLOR,
        fontWeight: '600',
        width: '20%',
    },
    labelText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.013,
        fontWeight: "600",
        textAlign: 'left',
        marginLeft: '5%',
        alignSelf: 'center',
        flex: 0.25,
    },
    input: {
        flex: 0.7,
        height: '50%',
        width: '50%',
        alignSelf: 'center',
        textAlign: 'left',
        fontSize: SCREEN_WIDTH * 0.015,
        color: HEADER_TITLE_COLOR,
        textShadowRadius: 10,
        paddingLeft: '3%',
        borderWidth: SCREEN_HEIGHT * 0.002,
        borderColor: BORDER_COLOR,
        borderRadius: SCREEN_WIDTH * 0.005,
        justifyContent: 'center',
    },
    button: {
        width: '60%',
        alignSelf: 'center',
        height: '30%',
        borderRadius: SCREEN_WIDTH * 0.005,
        backgroundColor: HEADER_TITLE_COLOR,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
    },
    icon: {
        width: undefined,
        height: '40%',
        tintColor: PRIMARY_BACKGROUND_COLOR,
        aspectRatio: 1,
        marginRight: '5%',
    },
    resultView: {
        flex: 0.01,
        position: 'absolute',
        bottom: '10%',
        alignSelf: 'center',
        width: '100%',
    },
    resultText: {
        textAlign: 'center',
        alignSelf: 'center',
        fontSize: SCREEN_WIDTH * 0.012,
        color: BORDER_COLOR,
        fontWeight: '600',
        width: '90%',
    },
});
